/**
 * Procedural role sprites — hand-drawn vector art on transparent canvas.
 * Used whenever an entity has no generated/cast art, so actors NEVER render
 * as raw boxes, spheres, capsules or cylinders.
 */

function shade(hex, f) {
  try {
    const h = hex.replace('#', '');
    const [r, g, b] = [0, 2, 4].map((i) => Math.min(255, Math.max(0, Math.round(parseInt(h.substr(i, 2), 16) * f))));
    return `rgb(${r},${g},${b})`;
  } catch {
    return hex;
  }
}

export function roleSpriteDataUrl(role, color = '#06B6D4') {
  const c = document.createElement('canvas');
  c.width = 256; c.height = 256;
  const x = c.getContext('2d');
  const base = color && color.startsWith('#') ? color : '#06B6D4';
  const lite = shade(base, 1.5);
  const dark = shade(base, 0.55);

  const glow = (cx, cy, r, col) => {
    const g = x.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, col);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = g;
    x.fillRect(cx - r, cy - r, r * 2, r * 2);
  };

  switch (role) {
    case 'player': {
      // Hooded adventurer silhouette with a bright scarf
      glow(128, 130, 120, 'rgba(255,255,255,0.10)');
      x.fillStyle = dark;
      x.beginPath(); // cloak
      x.moveTo(128, 30); x.quadraticCurveTo(196, 60, 186, 150);
      x.quadraticCurveTo(196, 220, 128, 232); x.quadraticCurveTo(60, 220, 70, 150);
      x.quadraticCurveTo(60, 60, 128, 30); x.fill();
      x.fillStyle = base;
      x.beginPath(); x.arc(128, 84, 34, 0, Math.PI * 2); x.fill(); // hood
      x.fillStyle = '#0D0D12';
      x.beginPath(); x.arc(128, 88, 22, 0, Math.PI * 2); x.fill(); // face shadow
      x.fillStyle = lite;
      x.beginPath(); x.arc(119, 86, 4.5, 0, Math.PI * 2); x.arc(137, 86, 4.5, 0, Math.PI * 2); x.fill(); // eyes
      x.fillStyle = lite;
      x.fillRect(96, 118, 64, 14); // scarf
      x.beginPath(); x.moveTo(150, 128); x.lineTo(176, 168); x.lineTo(158, 172); x.closePath(); x.fill();
      break;
    }
    case 'enemy': {
      // Horned imp silhouette with glaring eyes
      glow(128, 128, 118, 'rgba(255,60,60,0.12)');
      x.fillStyle = dark;
      x.beginPath();
      x.moveTo(128, 52); x.quadraticCurveTo(200, 70, 190, 150);
      x.quadraticCurveTo(198, 214, 128, 226); x.quadraticCurveTo(58, 214, 66, 150);
      x.quadraticCurveTo(56, 70, 128, 52); x.fill();
      x.fillStyle = base; // horns
      x.beginPath(); x.moveTo(84, 74); x.lineTo(58, 26); x.lineTo(100, 56); x.closePath(); x.fill();
      x.beginPath(); x.moveTo(172, 74); x.lineTo(198, 26); x.lineTo(156, 56); x.closePath(); x.fill();
      x.fillStyle = '#FFD34D'; // eyes
      x.beginPath(); x.moveTo(96, 112); x.lineTo(122, 104); x.lineTo(118, 124); x.closePath(); x.fill();
      x.beginPath(); x.moveTo(160, 112); x.lineTo(134, 104); x.lineTo(138, 124); x.closePath(); x.fill();
      x.fillStyle = '#0D0D12'; // jagged mouth
      x.beginPath(); x.moveTo(98, 168);
      for (let i = 0; i < 5; i++) { x.lineTo(104 + i * 12, i % 2 ? 168 : 180); }
      x.lineTo(158, 168); x.closePath(); x.fill();
      break;
    }
    case 'collectible': {
      // Glowing mote — a soft light, not a gem/coin
      glow(128, 128, 110, `rgba(255,255,255,0.16)`);
      const g = x.createRadialGradient(128, 128, 6, 128, 128, 72);
      g.addColorStop(0, '#FFFFFF');
      g.addColorStop(0.35, lite);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      x.fillStyle = g;
      x.beginPath(); x.arc(128, 128, 72, 0, Math.PI * 2); x.fill();
      x.strokeStyle = lite; x.lineWidth = 5; x.lineCap = 'round';
      [[128, 34, 128, 66], [128, 190, 128, 222], [34, 128, 66, 128], [190, 128, 222, 128]].forEach(([a, b, d, e]) => {
        x.beginPath(); x.moveTo(a, b); x.lineTo(d, e); x.stroke();
      });
      break;
    }
    case 'goal': {
      // Beacon archway with rising light
      glow(128, 100, 120, 'rgba(255,255,255,0.14)');
      x.fillStyle = dark;
      x.fillRect(58, 96, 26, 130); x.fillRect(172, 96, 26, 130); // pillars
      x.beginPath(); x.arc(128, 104, 70, Math.PI, 0); x.arc(128, 104, 44, 0, Math.PI, true); x.fill(); // arch
      const g = x.createLinearGradient(0, 226, 0, 96);
      g.addColorStop(0, lite);
      g.addColorStop(1, 'rgba(255,255,255,0)');
      x.fillStyle = g;
      x.fillRect(92, 104, 72, 122); // light curtain
      x.fillStyle = '#FFFFFF';
      x.beginPath(); x.arc(128, 88, 10, 0, Math.PI * 2); x.fill(); // beacon core
      break;
    }
    case 'obstacle': {
      // Hazards NEVER render a hardcoded star/spike — obstacles without art are
      // skipped at runtime and shown only as a neutral marker in the editor.
      break;
    }
    case 'decoration':
    case 'npc': {
      // Leafy tuft / standing figure hybrid marker with personality
      glow(128, 150, 100, 'rgba(255,255,255,0.08)');
      x.fillStyle = dark;
      x.beginPath(); x.ellipse(128, 196, 58, 26, 0, 0, Math.PI * 2); x.fill();
      x.strokeStyle = base; x.lineWidth = 12; x.lineCap = 'round';
      [[-30, -70], [0, -96], [30, -70]].forEach(([dx, dy]) => {
        x.beginPath(); x.moveTo(128, 190); x.quadraticCurveTo(128 + dx * 1.4, 190 + dy * 0.5, 128 + dx, 190 + dy); x.stroke();
      });
      x.fillStyle = lite;
      [[98, 120], [128, 94], [158, 120]].forEach(([cx, cy]) => {
        x.beginPath(); x.arc(cx, cy, 9, 0, Math.PI * 2); x.fill();
      });
      break;
    }
    default: {
      // Neutral marker — soft ring, never a solid block
      x.strokeStyle = base; x.lineWidth = 10;
      x.beginPath(); x.arc(128, 128, 76, 0, Math.PI * 2); x.stroke();
      x.fillStyle = lite;
      x.beginPath(); x.arc(128, 128, 18, 0, Math.PI * 2); x.fill();
    }
  }
  return c.toDataURL('image/png');
}
